import React, { useEffect, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import { Box, Button, Card, Dialog, DialogContent, DialogTitle, Grid, IconButton, Typography } from '@mui/material';
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';

import { getEquipmentInfoApi } from '@apis/index';
import { EquipmentInfoDialogInput, EquipmentInfoDialogState, MessageDialogState } from '@atom/DialogState';

import { GetEquipmentInfoQueryVariables } from '../../../API';

const images = {
  maxHeight: '100%',
  maxWidth: '100%',
  height: 'auto',
  width: 'auto',
  'align-items': 'center',
  display: 'flex',
  justifyContent: 'center'
};

const cards = {
  display: 'flex',
  justifyContent: 'center',
  'align-items': 'center',
  width: '100%',
  height: 'auto',
  maxHeight: '20vh'
};

const EquipmentDialog = () => {
  /* 共通State */
  const setMessageDialogState = useSetRecoilState(MessageDialogState);
  const resetEquipmentInfoDialogState = useResetRecoilState(EquipmentInfoDialogState);

  const equipmentInfoDialogState: EquipmentInfoDialogInput = useRecoilValue(EquipmentInfoDialogState);

  const [equipmentInfo, setEquipmentInfo] = useState<any>('');

  const { equipmentInfoData, onAddCartClick } = equipmentInfoDialogState;

  const { equipmentId, facilityId, num, priceIndex } = equipmentInfoData;

  const [itemNumber, setItemNumber] = useState<number>(num);
  const [unitPrice, setUnitPrice] = useState<number>(0);

  const handleClose = () => {
    resetEquipmentInfoDialogState();
  };

  // 備品情報取得
  const getData = async () => {
    try {
      const input: GetEquipmentInfoQueryVariables = {
        input: {
          equipmentId,
          facilityId
        }
      };

      // データ取得
      const result = await getEquipmentInfoApi(input);
      setEquipmentInfo(result);
      setItemNumber(num || 0);
      setUnitPrice(result.rentalPriceList?.[priceIndex]?.rentalPrice ?? 0);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error(error);

      let message = '時間をおいてから再度お試しください。解決できない場合は管理者へお問い合わせください。';
      if ('errors' in error && error.errors.length !== 0) {
        message = error.errors[0].message;
      }
      setMessageDialogState({
        open: true,
        messageData: {
          title: 'エラーが発生しました。',
          message
        }
      });
    }
  };

  useEffect(() => {
    if (equipmentId) {
      getData();
    }
  }, [equipmentId]);

  // 改行コードを置換する
  const replaceNewLine = (text?: string) => {
    if (!text) return undefined;
    const texts = text.split('\\n').map((item, index) => (
      <>
        {item}
        <br />
      </>
    ));

    return (
      <Typography variant="body1" component="p" textAlign="left">
        {texts}
      </Typography>
    );
  };

  return (
    <Dialog open={equipmentInfoDialogState.open} onClose={handleClose}>
      <DialogTitle sx={{ m: 0, p: 2 }} id="equipment-dialog-title">
        <Typography variant="h5" gutterBottom>
          {equipmentInfo.equipmentName}
        </Typography>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500]
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent>
      <Grid container spacing={2}>
        <Grid xs={12}>
          <Card style={{ padding: 16, ...images }} elevation={0}>
            <img
              src={`/images/${facilityId}/equipment/${equipmentInfo.equipmentImagePath}`}
              alt="equipmentImages"
              style={images}
            />
          </Card>
        </Grid>
        <Grid xs={12}>
          <Box>
            <Typography>{replaceNewLine(equipmentInfo.explanation)}</Typography>
          </Box>
        </Grid>

        {equipmentInfo.rentalPriceList?.map((price: any) => (
          // eslint-disable-next-line react/jsx-fragments
          <React.Fragment>
            <Grid xs={3} />
            <Grid xs={6}>
              <Typography>{price.rentalPriceDivision}</Typography>
            </Grid>
            <Grid xs={3}>
              <Typography sx={{ fontWeight: 'bold' }}>{price.rentalPrice}円</Typography>
            </Grid>
          </React.Fragment>
        ))}

        <Grid xs={12}>
          <Box sx={{ mt: 2, mb: 2 }}>
            <Box sx={{ width: '100%' }}>
              <Card sx={{ height: '35px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <IconButton onClick={() => setItemNumber(itemNumber - 1)}>
                  <RemoveIcon />
                </IconButton>
                <Typography>{itemNumber}</Typography>
                <IconButton onClick={() => setItemNumber(itemNumber + 1)}>
                  <AddIcon />
                </IconButton>
              </Card>
            </Box>
          </Box>
        </Grid>
        <Grid xs={12}>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => {
                onAddCartClick({ ...equipmentInfoData, itemNumber, unitPrice });
                handleClose();
              }}
            >
              カートに追加
            </Button>
          </Box>
          <Box>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                handleClose();
              }}
            >
              閉じる
            </Button>
          </Box>
        </Grid>
      </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default EquipmentDialog;
